import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { apiClient } from '@/services/apiClient'
import { UserCircleIcon, KeyIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

interface OperatorAccount {
  balance: number
  commission_balance: number
  total_commission: number
  total_withdraw: number
  pending_commission: number
}

export default function ProfilePage() {
  const { user } = useAuthStore()
  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState<'info' | 'password'>(
    tabParam === 'password' ? 'password' : 'info'
  )
  const [operatorAccount, setOperatorAccount] = useState<OperatorAccount | null>(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // 修改密码表单
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  useEffect(() => {
    if (user?.role === 'operator') {
      fetchOperatorAccount()
    }
  }, [user])

  const fetchOperatorAccount = async () => {
    if (!user?.id) return
    
    try {
      const response = await apiClient.getOperatorDetail(user.id)
      if (response.success && response.data?.account) {
        setOperatorAccount(response.data.account)
      }
    } catch (error) {
      console.error('Failed to fetch operator account:', error)
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: '两次输入的新密码不一致' })
      return
    }

    if (passwordForm.newPassword.length < 6) {
      setMessage({ type: 'error', text: '新密码长度至少为6位' })
      return
    }

    setLoading(true)
    setMessage(null)

    try {
      // TODO: 调用修改密码 API
      // const response = await apiClient.changePassword({
      //   currentPassword: passwordForm.currentPassword,
      //   newPassword: passwordForm.newPassword
      // })
      
      // 模拟成功
      setTimeout(() => {
        setMessage({ type: 'success', text: '密码修改成功' })
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
        setLoading(false)
      }, 1000)
    } catch (error) {
      setMessage({ type: 'error', text: '密码修改失败，请重试' })
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
    }).format(value)
  }

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: '超级管理员',
      finance: '财务管理员',
      audit: '审计管理员',
      operator: '运营商'
    }
    return labels[role] || role
  }

  if (!user) {
    return (
      <div className="page-content">
        <div className="text-center py-12">
          <p className="text-gray-500">请先登录</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">个人中心</h1>
        <p className="text-sm text-gray-600 mt-1">查看和管理您的账户信息</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('info')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'info'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UserCircleIcon className="w-5 h-5 inline-block mr-2" />
            账户资料
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'password'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <KeyIcon className="w-5 h-5 inline-block mr-2" />
            修改密码
          </button>
        </nav>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' && <CheckCircleIcon className="w-5 h-5 mr-2" />}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* Content */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 基本信息 */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">基本信息</h2>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">姓名</label>
                <p className="text-gray-900 mt-1">{user.full_name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">邮箱</label>
                <p className="text-gray-900 mt-1">{user.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">角色</label>
                <p className="text-gray-900 mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {getRoleLabel(user.role)}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">账户状态</label>
                <p className="text-gray-900 mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    正常
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* 运营商账户信息 */}
          {user.role === 'operator' && operatorAccount && (
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold">账户信息</h2>
              </div>
              <div className="card-body space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">账户余额</label>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    {formatCurrency(operatorAccount.balance)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">用于创建用户和充值赠金</p>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <label className="text-sm font-medium text-gray-600">佣金余额</label>
                  <p className="text-2xl font-bold text-green-900 mt-1">
                    {formatCurrency(operatorAccount.commission_balance)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">来自用户交易手续费分成</p>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <label className="text-xs font-medium text-gray-600">累计佣金</label>
                    <p className="text-lg font-semibold text-green-600 mt-1">
                      {formatCurrency(operatorAccount.total_commission)}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">累计提现</label>
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {formatCurrency(operatorAccount.total_withdraw)}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">待结佣金</label>
                  <p className="text-lg font-semibold text-orange-600 mt-1">
                    {formatCurrency(operatorAccount.pending_commission)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'password' && (
        <div className="max-w-2xl">
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">修改密码</h2>
            </div>
            <div className="card-body">
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    当前密码 *
                  </label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请输入当前密码"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    新密码 *
                  </label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请输入新密码（至少6位）"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    确认新密码 *
                  </label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请再次输入新密码"
                    required
                    minLength={6}
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? '修改中...' : '确认修改'}
                  </button>
                </div>

                <div className="text-sm text-gray-500 mt-4">
                  <p>密码要求：</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>长度至少6位</li>
                    <li>建议包含字母、数字和特殊字符</li>
                  </ul>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
